/**
 * <tessi-hero-video> — the background video on the TESSI homepage top hero.
 * Added 2026-07-29.
 *
 * WHY THIS EXISTS AS A CUSTOM ELEMENT, not an inline <script>:
 *   CLAUDE.md's number one recurring trap is that inline scripts DO NOT EXECUTE when
 *   Shopify re-renders a section in the theme editor. A custom element is upgraded in
 *   any markup the browser parses, re-renders included, so the video comes back on its
 *   own after she changes a homepage setting. assets/video-background.js already uses
 *   this pattern in this theme.
 *
 * WHY IT DOES NOT REUSE <hero-video> (assets/hero-video.js):
 *   That element's connectedCallback calls attemptPlay() immediately and gives up after
 *   3 backoff retries with {once:true} gesture listeners, so against a src-less <video>
 *   it would burn every retry before our source lands. It also has no viewport gate and
 *   no fade. Four heroes in sections/hero.liquid depend on it; it must not be touched.
 *
 * CONTRACT WITH sections/TESSI-home.liquid:
 *   - The <video> ships with NO src and NO <source>. A video with no source never issues
 *     a request. This element is the only thing that ever assigns one, and it decides
 *     WHICH of two different files from the viewport. That is the only approach that
 *     actually works: `media` on <source> is ignored for video, and CSS display:none does
 *     not stop a download.
 *   - The element stays opacity:0 until the video is genuinely PLAYING, so every failure
 *     path falls back to the hand-cropped photo underneath, which is the hero that is
 *     live today. NEVER reveal on 'canplay' or 'loadeddata': a blocked autoplay would
 *     leave a frozen bright frame on screen instead of the photo.
 *   - GATE and MOBILE_GATE must stay in sync with the two media queries in
 *     TESSI-home.liquid's <style> block.
 *
 * TWO PROFILES, added 2026-07-29 when her portrait phone cut landed:
 *   desktop  data-src / data-src-sd            1920x1080 landscape, 1080p, ~39 MB
 *   mobile   data-src-mobile / -mobile-sd       404x720  portrait,   720p, 24.6 MB
 * They are SEPARATE FILES, not rungs of one upload. Between the two gates (768-1023px,
 * and 1024px+ without hover) no src is ever assigned and the photo is the hero.
 *
 * ⚠️ THE PROFILES DIFFER IN *WHEN*, NOT JUST *WHAT*, AND THAT IS THE POINT:
 *   Desktop assigns its src immediately, as it has since it shipped.
 *   Mobile assigns NOTHING until `window.load` has fired and the browser has then gone
 *   idle. A phone's bandwidth is the scarce thing on this page: the hero photo is the
 *   LCP element, and a 24.6 MB video racing it would push LCP out for the sake of
 *   something that is invisible until it plays anyway. Deferring costs the design
 *   nothing, because the photo IS the first frame's stand-in, and it means the video
 *   cannot regress a single load metric. Do not "simplify" this back to an immediate
 *   assignment.
 */

/**
 * ⚠️ `hover: hover` is what actually excludes iPads, including a 1366px landscape iPad
 * Pro that min-width alone would let through onto cellular. Hybrid touch laptops with a
 * mouse report hover:hover and correctly get the video.
 * ⚠️ 1024px, deliberately NOT the 768px mobile breakpoint used elsewhere in this theme:
 * 768 is exactly portrait-iPad width, the worst possible place to draw this line.
 */
const GATE = '(min-width: 1024px) and (hover: hover)';

/**
 * ⚠️ 767px, which IS the breakpoint the rest of this theme calls mobile, on purpose: the
 * phone hero's own CSS (`#m-hero-content`'s -190px lift, the full-bleed `.hero-inset`
 * reset) all live at max-width 767px, so gating the video anywhere else would let the
 * video and the type sitting on it disagree about what a phone is.
 * ⚠️ No `hover` clause here. Deliberate: a desktop browser dragged narrower than 768px
 * would then be excluded by BOTH gates and lose the hero video entirely, which looks
 * like a bug. At that width the portrait cut is the better-fitting file anyway.
 */
const MOBILE_GATE = '(max-width: 767px)';

class TessiHeroVideo extends HTMLElement {
  connectedCallback() {
    this.gone = false; // cleared here so a re-attached element can start again
    this.maybeStart();

    // ⚠️ Re-check when the viewport crosses EITHER gate. Without this, someone who loads
    // the page in a narrow window and then maximises it gets the photo forever, because
    // connectedCallback already ran and returned. Caught in testing on 2026-07-29.
    // ⚠️ Both queries are watched, not just the desktop one. A window dragged across
    // 767px in either direction has to be able to start the phone profile too.
    // This can only ever ADD a video on a viewport that newly satisfies a gate, and
    // `started` below means it happens at most once, so nothing is ever downloaded twice.
    this.gateQueries = [window.matchMedia(GATE), window.matchMedia(MOBILE_GATE)];
    this.onGateChange = () => this.maybeStart();
    this.gateQueries.forEach((q) => {
      if (q.addEventListener) q.addEventListener('change', this.onGateChange);
    });
  }

  /**
   * Decides IF we load, WHICH file, and WHEN. Cheap and safe to call repeatedly: it is
   * called once on connect and again on every gate change.
   */
  maybeStart() {
    // `started` covers a src already assigned; `pending` covers one scheduled but not yet
    // assigned, which is the whole mobile path. Without the second flag, a resize while
    // the phone profile was waiting for `load` would queue a second start.
    if (this.dataset.started || this.pending) return;

    const video = this.querySelector('video');
    if (!video) return;

    // Every "should we load this at all" decision happens before a single byte moves.
    const isDesktop = window.matchMedia(GATE).matches;
    const isMobile = window.matchMedia(MOBILE_GATE).matches;
    // The deliberate gap: tablets, and anything between the two gates. Photo only.
    if (!isDesktop && !isMobile) return;

    // Both profiles honour these. Reduced motion also has a display:none !important rule
    // in the CSS; this is the belt to that pair of braces, and it saves the bytes too.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const conn = navigator.connection || {};
    if (conn.saveData) return;

    const effective = conn.effectiveType || '';
    const verySlow = /(^|-)2g$/.test(effective); // matches '2g' and 'slow-2g'

    let src;
    if (isMobile) {
      // ⚠️ Phones diverge from desktop here: on 2g they get NOTHING, where desktop drops
      // to its SD rung. Desktop's "slow" is a slow wifi line; a phone reporting 2g is on
      // a metered cellular connection that would take minutes to buffer even the 8.7 MB
      // cut, long after she has been scrolled past. The photo is the better hero there.
      if (verySlow) return;
      src = (effective === '3g' && this.dataset.srcMobileSd) || this.dataset.srcMobile;
    } else {
      src = (verySlow && this.dataset.srcSd) || this.dataset.src;
    }
    if (!src) return;

    // ⚠️ THE ONE ASYMMETRY THAT MATTERS. Desktop starts now; the phone waits until the
    // page has finished loading AND the browser is idle, so the video can never take
    // bandwidth from the LCP photo, the fonts, or anything else on the critical path.
    if (isMobile) {
      this.pending = true;
      this.whenIdle(() => {
        this.pending = false;
        // Re-check everything: the page may have been rotated, resized or re-rendered
        // in the time we spent waiting for load and then for idle.
        if (this.gone || this.dataset.started) return;
        if (!window.matchMedia(MOBILE_GATE).matches) {
          // The viewport left the phone gate while we waited. If it crossed INTO the
          // desktop gate during that window, the gate-change event for it was swallowed
          // by the `pending` guard above, so re-run the decision rather than dropping
          // it: desktop then starts (immediately, as it should), anything else returns.
          this.maybeStart();
          return;
        }
        this.begin(video, src);
      });
      return;
    }

    this.begin(video, src);
  }

  /**
   * Runs `fn` once the page has fully loaded and the browser has a spare moment.
   * ⚠️ The readyState branch is not optional. Module scripts execute BEFORE `load`, so on
   * a normal page view the listener is the path taken; but a Shopify theme-editor
   * re-render connects this element long AFTER `load` has fired, and a plain
   * addEventListener('load') there would wait forever and the video would never appear.
   */
  whenIdle(fn) {
    const idle = () => {
      // The 2s timeout keeps a genuinely busy main thread from postponing this forever.
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(fn, { timeout: 2000 });
      } else {
        setTimeout(fn, 200); // Safari < 17.4 has no requestIdleCallback.
      }
    };
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  /** Commits: assigns the src and wires playback. Past this point bytes move. */
  begin(video, src) {
    this.dataset.started = '1';
    this.video = video;
    this.playing = false;
    this.retries = 0;

    // Set these as PROPERTIES as well as attributes. Safari has historically ignored the
    // muted attribute on an element whose src is assigned after parse, and an unmuted
    // video is refused autoplay by every browser.
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    // Last line of defence for the play button: even if something adds `controls`, it
    // comes straight back off.
    video.removeAttribute('controls');

    this.onPlaying = () => {
      this.playing = true;
      this.retries = 0;
      this.classList.add('is-playing'); // the 600ms fade in
    };
    this.onPause = () => {
      this.playing = false;
    };
    // Only a hard error hides it again. Deliberately NOT listening to 'waiting' or
    // 'stalled': a buffer hiccup would flash the photo back mid-playback.
    this.onError = () => this.classList.remove('is-playing');

    video.addEventListener('playing', this.onPlaying);
    video.addEventListener('pause', this.onPause);
    video.addEventListener('error', this.onError);

    video.src = src;
    video.load();

    // Pause once the hero is scrolled past. Bounds the battery and CPU cost of a 44s
    // forever-loop to the time the hero is actually on screen.
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.attemptPlay();
          else if (this.playing) video.pause();
        }
      },
      { threshold: 0 }
    );
    this.observer.observe(video);

    this.onVisibility = () => {
      if (document.visibilityState === 'visible') {
        this.retries = 0;
        this.attemptPlay();
      }
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    // A first user gesture lifts autoplay restrictions in the strictest browsers.
    this.onGesture = () => this.attemptPlay();
    document.addEventListener('click', this.onGesture, { once: true });

    this.attemptPlay();
  }

  attemptPlay() {
    if (!this.video || this.playing) return;
    const p = this.video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        if (++this.retries <= 3) {
          setTimeout(() => this.attemptPlay(), 400 * Math.pow(2, this.retries - 1));
        }
        // After 3 failures we stop and leave the photo showing. Never surface a control.
      });
    }
  }

  disconnectedCallback() {
    // ⚠️ Checked by the deferred mobile start before it commits. Without it, an element
    // torn out during a theme-editor re-render while it was still waiting for `load`
    // would come back on the idle callback and download the video into a detached node.
    this.gone = true;
    if (this.observer) this.observer.disconnect();
    (this.gateQueries || []).forEach((q) => {
      if (q && q.removeEventListener) q.removeEventListener('change', this.onGateChange);
    });
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('click', this.onGesture);
    if (this.video) {
      this.video.removeEventListener('playing', this.onPlaying);
      this.video.removeEventListener('pause', this.onPause);
      this.video.removeEventListener('error', this.onError);
    }
  }
}

if (!customElements.get('tessi-hero-video')) {
  customElements.define('tessi-hero-video', TessiHeroVideo);
}
